import {
  graphQLErrorCode,
  Phone,
  useChangeWorkspace,
  useCurrentAccount,
  useResolveAssetShareLink,
  useSendAuthCode,
  useStorageKeys,
  useWorkspace,
  useTheme,
} from '@gripp/shared-logic';
import {
  createBaseTheme,
  Colors,
  EditProfileForm,
  InvitationAccepted,
  InvitationIntro,
  LoadingIndicator,
  SaveFormTop,
  Spacing,
  TopModalBar,
  useAcceptInvitationForm,
} from '@gripp/shared-ui';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useErrorBoundary } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';
import { Dimensions, Modal, StyleSheet, View } from 'react-native';
import { PaperProvider } from 'react-native-paper';
import { useNavigate, useParams } from 'react-router-dom';
import {
  sharedAssetLanding,
  ShareRefusalMessage,
} from '../assets/components/joinSharedAssetView';

type ResolvedShareTarget = {
  assetId?: string | null;
  workspaceId?: string | null;
};

/** Which refusal a share recipient is looking at. */
type ShareRefusal = 'removed' | 'generic';

/**
 * The create-profile page, reached with either grant: a workspace invitation
 * (`/acceptInvitation/:invitationId/:workspaceId`) or an asset share link
 * (`/acceptInvitation/share/:shareToken`). Signed in, the grant is accepted
 * for the account on open; signed out, the profile form's Join creates one.
 *
 * The two grants part ways inside `useAcceptInvitationForm`: a share token is
 * redeemed through `redeemShareLink` and never through `acceptInvitation`,
 * whose role selection falls through to Workspace Admin. What this page adds
 * for a share link is where the recipient goes afterwards — the asset — and
 * the refusals a link can raise, which an invitation cannot.
 */
export default function AcceptInvitation() {
  const { t } = useTranslation();
  const { resolvedTheme } = useTheme();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const {
    invitationId,
    workspaceId: invitedWorkspaceId,
    shareToken,
  } = useParams();
  const { account, isLoading } = useCurrentAccount();
  const [modalVisible, setModalVisible] = useState(false);
  const [visibleScreen, setVisibleScreen] = useState('intro');
  const [refusal, setRefusal] = useState<ShareRefusal | null>(null);
  const { showBoundary } = useErrorBoundary();
  const storageKeys = useStorageKeys();
  const { sendAuthCodeAsync } = useSendAuthCode();
  const { mutateAsync: changeWorkspace } = useChangeWorkspace();

  // A share link names its own workspace and asset, resolved server-side and
  // readable before anyone joins. Off on the invitation path.
  const { data: shareData, isError: isShareError } = useResolveAssetShareLink(
    shareToken
  ) as {
    data?: { share?: { resolveShareLink?: ResolvedShareTarget } };
    isError: boolean;
  };
  const target = shareData?.share?.resolveShareLink;
  const workspaceId = shareToken ? target?.workspaceId : invitedWorkspaceId;
  const workspace = useWorkspace(workspaceId as string);

  // Both ids go null together once the workspace-asset, the asset or the
  // workspace is deleted; the link still resolves, and redeeming would be
  // refused.
  const shareUnavailable =
    !!shareToken && !!target && (!target.workspaceId || !target.assetId);
  // The redeem waits for the resolve: the destination is read off it.
  const shareReady = !shareToken || !!(target?.workspaceId && target?.assetId);
  const shareMessage = refusal
    ? refusal === 'removed'
      ? 'chat.join.removedError'
      : 'chat.join.error'
    : isShareError
    ? 'chat.join.resolveError'
    : shareUnavailable
    ? 'chat.join.unavailable'
    : null;

  // Only prompt sign-up when the visitor is NOT already logged in. A logged-in
  // account is accepted automatically below, so the modal must never appear.
  useEffect(() => {
    if (!workspace || isLoading || account) return;
    const timer = setTimeout(() => {
      setModalVisible(true);
    }, 2000);
    return () => clearTimeout(timer);
  }, [workspace, account, isLoading]);

  const onAccept = async (
    success: boolean,
    phone: Phone | undefined,
    email: string
  ) => {
    if (shareToken) {
      // The account exists or was just created, and its code is already on
      // its way (the form sent it). Signing in returns the recipient to the
      // share link, which redeems for the now-signed-in account and lands on
      // the asset. `/share/:token` writes these same keys before sending a
      // visitor here; written again so a direct hit on this page completes
      // the trip too. `pendingDestination` is the durable copy: a token
      // arriving deletes `redirectPath` before the app has landed anywhere.
      const sharePath = `/share/${shareToken}`;
      localStorage.setItem(storageKeys.redirectPath, sharePath);
      localStorage.setItem(storageKeys.pendingDestination, sharePath);
      navigate('/verify-auth-code');
      return;
    }

    const input = {
      phone,
      email,
    };

    await sendAuthCodeAsync(input, () => navigate('/verify-auth-code'));
  };

  const onAuthenticatedAccept = async (success: boolean) => {
    // Surface a failed acceptance instead of routing home as if it worked.
    if (!success) {
      showBoundary(new Error('Failed to accept invitation'));
      return;
    }

    if (shareToken) {
      if (!target?.workspaceId || !target?.assetId) {
        setRefusal('generic');
        return;
      }
      // Redeemed; now the switch and the landing, through the same helper
      // `/share/:token` uses for a signed-in recipient, so the two cannot
      // pick different destinations. A hard navigation, because the account's
      // permissions just changed and the route gates read them off the
      // Account built at load.
      const destination = await sharedAssetLanding({
        workspaceId: target.workspaceId,
        assetId: target.assetId,
        accountId: account?.id,
        activeWorkspaceId: account?.workspace?.id,
        changeWorkspace,
      });
      window.location.assign(destination);
      return;
    }

    await queryClient.invalidateQueries({ queryKey: ['currentAccount'] });
    navigate('/');
  };

  const { control, handleSubmit, formState } = useAcceptInvitationForm({
    invitationId,
    shareToken,
    onAccept: account ? onAuthenticatedAccept : onAccept,
    account,
  });

  // A share refusal is rendered in place, never thrown to the boundary.
  // `FORBIDDEN` is redeemShareLink's code for a caller on this workspace's
  // removedWorkspaces; every other refusal it raises is `NOT_FOUND`. Read the
  // code, never the message.
  const onShareRefused = (err: unknown) =>
    setRefusal(graphQLErrorCode(err) === 'FORBIDDEN' ? 'removed' : 'generic');

  // Logged in: auto-accept for this account and route on. Keyed on account id
  // (fires once the session account resolves), ref-guarded against double-submit.
  // Keyed on the grant, not a bare flag: react-router reuses this component
  // when only the param changes, so a second link opened in the same session
  // would find a one-way latch already set and never auto-accept.
  const grantKey = shareToken ?? invitationId;
  const acceptedGrantRef = useRef<string>();
  useEffect(() => {
    if (!account?.id || !shareReady || acceptedGrantRef.current === grantKey)
      return;
    acceptedGrantRef.current = grantKey;
    handleSubmit().catch(shareToken ? onShareRefused : showBoundary);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account?.id, shareReady, grantKey]);

  return (
    <PaperProvider theme={createBaseTheme(false, resolvedTheme)}>
      {shareMessage ? (
        <ShareRefusalMessage testID="accept-share-message">
          {t(shareMessage)}
        </ShareRefusalMessage>
      ) : shareToken && !target ? (
        // A share link's workspace is known only once the link resolves, and a
        // token that turns out to be bad takes the query's retries to say so
        // — an intro with no workspace name is not what to show meanwhile.
        <LoadingIndicator />
      ) : (
        visibleScreen === 'intro' && (
          <InvitationIntro workspace={workspace} t={t} />
        )
      )}
      {visibleScreen === 'accepted' && <InvitationAccepted t={t} />}
      <Modal visible={modalVisible && !account && !shareMessage}>
        <View style={styles.modalContainer}>
          <View style={styles.contentContainer}>
            <SaveFormTop
              title={t('invitation.pending.title')}
              saveLabel={t('invitation.pending.join')}
              onSubmit={handleSubmit}
              hasBorder={true}
              onCancel={() => navigate('/login')}
              topBar={TopModalBar}
              isDirty={formState.isDirty}
              isValid={formState.isValid}
            >
              <View style={styles.formContainer}>
                <EditProfileForm
                  control={control}
                  inputMaxWidth={520}
                  autoFocus
                />
              </View>
            </SaveFormTop>
          </View>
        </View>
      </Modal>
    </PaperProvider>
  );
}

const styles = StyleSheet.create({
  modalContainer: {
    flex: 1,
    justifyContent: 'flex-start',
    alignItems: 'center',
    paddingTop: Dimensions.get('window').height / 10,
    backgroundColor: Colors.role.surfaceActive,
  },
  contentContainer: {
    backgroundColor: Colors.role.surfaceHover,
    width: 560,
    borderRadius: 12,
  },
  formContainer: {
    paddingHorizontal: Spacing.basePadding.paddingHorizontal,
    height: '100%',
    width: '100%',
  },
});
